// ClientRegistrationStep1.jsx - 郵便番号住所検索対応版
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { 
  TextField, 
  Button, 
  Container, 
  Typography, 
  Paper, 
  Box,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  InputAdornment,
  IconButton
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';

const MAX_NAME_LENGTH = 50;
const MIN_NAME_LENGTH = 2;
const FORBIDDEN_REGEX = /[<>"'`;\\\/*?&|^$()[\]{}:\x00-\x1F\x7F]/;

// 連絡先情報用定数
const MAX_PHONE_LENGTH = 20;
const MAX_EMAIL_LENGTH = 255;
const PHONE_PATTERN = /^[0-9+()\\-\\s]+$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// 郵便番号パターン（ハイフンあり・なし両対応）
const ZIPCODE_PATTERN = /^(\d{3}-?\d{4})$/;

const ClientRegistrationStep1 = () => {
    const [clientData, setClientData] = useState({ 
        name: "", 
        zipcode: "",   // 郵便番号追加
        address: "",
        phone: "",
        email: ""
    });
    const [errorMessages, setErrorMessages] = useState({
        name: "",
        zipcode: "",   // 郵便番号エラー追加
        address: "",
        phone: "",
        email: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSearchingAddress, setIsSearchingAddress] = useState(false);
    const [apiError, setApiError] = useState("");
    const [addressSearchResult, setAddressSearchResult] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fromStep2 = sessionStorage.getItem("fromStep2");
        const fromDashboard = sessionStorage.getItem("fromDashboard");
        if (fromStep2 === "true") {
            const storedClientData = sessionStorage.getItem("clientData");
            if (storedClientData) {
                setClientData(JSON.parse(storedClientData));
            }
        } else if (fromDashboard === "true") {
            sessionStorage.removeItem("clientData");
            sessionStorage.removeItem("fromStep2");
            sessionStorage.removeItem("fromDashboard");
            setClientData({ name: "", zipcode: "", address: "", phone: "", email: "" });
        }
    }, [location.pathname]);

    // 郵便番号による住所検索
    const searchAddressByZipcode = async (zipcode) => {
        // 郵便番号の正規化（ハイフンを除去）
        const normalizedZipcode = zipcode.replace(/-/g, '');
        
        if (!ZIPCODE_PATTERN.test(zipcode)) {
            setErrorMessages(prev => ({ 
                ...prev, 
                zipcode: "郵便番号は7桁で入力してください（例：123-4567）" 
            }));
            return;
        }

        setIsSearchingAddress(true);
        setAddressSearchResult("");
        setErrorMessages(prev => ({ ...prev, zipcode: "" }));

        try {
            const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${normalizedZipcode}`);
            const data = await response.json();

            if (data.status === 200 && data.results && data.results.length > 0) {
                const result = data.results[0];
                const fullAddress = `${result.address1}${result.address2}${result.address3}`;
                
                // 住所フィールドに自動入力
                setClientData(prev => ({ ...prev, address: fullAddress }));
                setAddressSearchResult(`✓ 住所が見つかりました: ${fullAddress}`);
                
                // 住所エラーをクリア
                setErrorMessages(prev => ({ ...prev, address: "" }));
            } else {
                setErrorMessages(prev => ({ 
                    ...prev, 
                    zipcode: "該当する住所が見つかりませんでした" 
                }));
            }
        } catch (error) {
            setErrorMessages(prev => ({ 
                ...prev, 
                zipcode: "住所検索でエラーが発生しました。しばらく経ってから再試行してください。" 
            }));
        } finally {
            setIsSearchingAddress(false);
        }
    };

    // 郵便番号入力時の自動検索
    const handleZipcodeChange = (value) => {
        setClientData({ ...clientData, zipcode: value });
        setAddressSearchResult("");
        
        // 7桁入力完了時に自動検索
        const normalizedValue = value.replace(/-/g, '');
        if (normalizedValue.length === 7 && /^\d{7}$/.test(normalizedValue)) {
            searchAddressByZipcode(value);
        } else if (value.trim() === "") {
            setErrorMessages(prev => ({ ...prev, zipcode: "" }));
        }
    };

    // 顧客名のエラーメッセージ生成
    const getNameErrorMessage = (value) => {
        if (value.length < MIN_NAME_LENGTH) {
            return "顧客名は2文字以上で入力してください。";
        }
        if (value.length > MAX_NAME_LENGTH) {
            return "顧客名は50文字以内で入力してください。";
        }
        if (FORBIDDEN_REGEX.test(value)) {
            if (value.includes("(") || value.includes(")")) {
                return "半角括弧が含まれています。「（株）」などは全角で入力してください。";
            }
            return "使用できない文字が含まれています（< > \" ' など）。";
        }
        return "";
    };

    // 電話番号のエラーメッセージ生成
    const getPhoneErrorMessage = (value) => {
        if (!value || !value.trim()) {
            return "";
        }
        
        const trimmed = value.trim();
        if (trimmed.length > MAX_PHONE_LENGTH) {
            return "電話番号は20文字以内で入力してください。";
        }
        if (!PHONE_PATTERN.test(trimmed)) {
            return "電話番号に使用できない文字が含まれています。";
        }
        
        const digitsOnly = trimmed.replace(/[^0-9]/g, "");
        if (digitsOnly.length > 0 && digitsOnly.length < 7) {
            return "電話番号の桁数が短すぎます。";
        }
        
        return "";
    };

    // メールアドレスのエラーメッセージ生成
    const getEmailErrorMessage = (value) => {
        if (!value || !value.trim()) {
            return "";
        }
        
        const trimmed = value.trim();
        if (trimmed.length > MAX_EMAIL_LENGTH) {
            return "メールアドレスは255文字以内で入力してください。";
        }
        
        if (/[<>"'`]/.test(trimmed)) {
            return "メールアドレスに使用できない文字が含まれています（< > \" ' ` など）。";
        }
        
        if (!EMAIL_PATTERN.test(trimmed)) {
            return "メールアドレスの形式が正しくありません（例：example@company.com）。";
        }
        if (trimmed.includes("..")) {
            return "メールアドレスに連続したドットが含まれています。";
        }
        
        const atCount = (trimmed.match(/@/g) || []).length;
        if (atCount !== 1) {
            return "メールアドレスの形式が正しくありません。";
        }
        
        return "";
    };

    // 住所のエラーメッセージ生成
    const getAddressErrorMessage = (value) => {
        if (value.length > 100) {
            return "住所は100文字以内で入力してください。";
        }
        if (FORBIDDEN_REGEX.test(value)) {
            return "使用できない文字が含まれています（< > \" 改行など）。";
        }
        return "";
    };

    // 入力値変更ハンドラー
    const handleInputChange = (field, value) => {
        if (field === "zipcode") {
            handleZipcodeChange(value);
            return;
        }
        
        setClientData({ ...clientData, [field]: value });
        
        const newErrors = { ...errorMessages };
        
        switch (field) {
            case "name":
                if (value.trim() === "") {
                    newErrors.name = "";
                } else {
                    newErrors.name = getNameErrorMessage(value);
                }
                break;
            case "address":
                newErrors.address = getAddressErrorMessage(value);
                setAddressSearchResult(""); // 手動編集時は検索結果をクリア
                break;
            case "phone":
                newErrors.phone = getPhoneErrorMessage(value);
                break;
            case "email":
                newErrors.email = getEmailErrorMessage(value);
                break;
            default:
                break;
        }
        
        setErrorMessages(newErrors);
    };

    // 送信前の最終バリデーション
    const validateBeforeSubmit = () => {
        const name = clientData.name.trim();
        
        if (!name) {
            setErrorMessages(prev => ({ ...prev, name: "顧客名を入力してください。" }));
            return false;
        }
        
        if (name.length < MIN_NAME_LENGTH) {
            setErrorMessages(prev => ({ ...prev, name: "顧客名は2文字以上で入力してください。" }));
            return false;
        }
        
        const nameError = getNameErrorMessage(name);
        const phoneError = getPhoneErrorMessage(clientData.phone);
        const emailError = getEmailErrorMessage(clientData.email);
        const addressError = getAddressErrorMessage(clientData.address);
        
        if (nameError || phoneError || emailError || addressError) {
            setErrorMessages({
                name: nameError,
                zipcode: "",
                phone: phoneError,
                email: emailError,
                address: addressError
            });
            return false;
        }
        
        return true;
    };

    const handleSubmit = async () => {
        if (!validateBeforeSubmit()) {
            return;
        }

        setIsSubmitting(true);
        setApiError("");

        try {
            const token = sessionStorage.getItem("token");
            if (!token) {
                navigate("/login");
                return;
            }

            const response = await fetch("/api/clients", {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    name: clientData.name.trim(),
                    address: clientData.address.trim() || null,
                    phone: clientData.phone.trim() || null,
                    email: clientData.email.trim() || null,
                    groupIds: []
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || "顧客の登録に失敗しました");
            }

            const data = await response.json();
            
            sessionStorage.setItem("registrationSuccess", "true");
            sessionStorage.setItem("registeredClientId", data.id);
            sessionStorage.setItem("registeredClientName", clientData.name.trim());
            
            navigate("/client-registration-step2");

        } catch (error) {
            setApiError(error.message);
            setIsSubmitting(false);
        }
    };

    // 送信ボタンの有効/無効判定
    const isSubmitDisabled = () => {
        const name = clientData.name.trim();
        const hasErrors = Object.values(errorMessages).some(msg => msg);
        return !name || 
               name.length < MIN_NAME_LENGTH || 
               hasErrors || 
               isSubmitting;
    };

    return (
        <>
            <Header />
            <Container 
                maxWidth="sm" 
                sx={{ 
                    mt: 4,
                    pb: { xs: 12, sm: 6 },
                    minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="h5" fontWeight="medium">
                            顧客情報の登録
                        </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        顧客の基本情報を入力してください
                    </Typography>

                    {apiError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {apiError}
                        </Alert>
                    )}

                    {/* 基本情報セクション */}
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="primary" />
                        基本情報
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="顧客名"
                                fullWidth
                                placeholder="顧客名を入力（2文字以上）"
                                value={clientData.name}
                                onChange={(e) => handleInputChange("name", e.target.value)}
                                error={!!errorMessages.name}
                                helperText={
                                    errorMessages.name || 
                                    `${clientData.name.length}/50 文字 ${clientData.name.length < MIN_NAME_LENGTH && clientData.name.length > 0 ? '（あと' + (MIN_NAME_LENGTH - clientData.name.length) + '文字）' : ''}`
                                }
                                required
                                size="small"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        minHeight: { xs: 56, sm: 48 }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* 住所情報セクション */}
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HomeIcon fontSize="small" color="primary" />
                        住所情報
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="郵便番号"
                                fullWidth
                                placeholder="123-4567"
                                value={clientData.zipcode}
                                onChange={(e) => handleInputChange("zipcode", e.target.value)}
                                error={!!errorMessages.zipcode}
                                helperText={errorMessages.zipcode || "例：123-4567（自動で住所を検索します）"}
                                size="small"
                                InputProps={{
                                    endAdornment: isSearchingAddress ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={16} />
                                        </InputAdornment>
                                    ) : (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => searchAddressByZipcode(clientData.zipcode)}
                                                disabled={!clientData.zipcode.trim() || isSearchingAddress}
                                                size="small"
                                            >
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        minHeight: { xs: 56, sm: 48 }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="住所"
                                fullWidth
                                placeholder="住所を入力（郵便番号から自動入力可能）"
                                value={clientData.address}
                                onChange={(e) => handleInputChange("address", e.target.value)}
                                error={!!errorMessages.address}
                                helperText={errorMessages.address}
                                size="small"
                                InputProps={{
                                    startAdornment: <HomeIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        minHeight: { xs: 56, sm: 48 }
                                    }
                                }}
                            />
                        </Grid>

                        {addressSearchResult && (
                            <Grid item xs={12}>
                                <Alert severity="success" sx={{ py: 1 }}>
                                    <Typography variant="body2">
                                        {addressSearchResult}
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* 連絡先情報セクション */}
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalPhoneIcon fontSize="small" color="primary" />
                        連絡先情報（任意）
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="電話番号"
                                fullWidth
                                placeholder="例：090-1234-5678"
                                value={clientData.phone}
                                onChange={(e) => handleInputChange("phone", e.target.value)}
                                error={!!errorMessages.phone}
                                helperText={errorMessages.phone || "例：090-1234-5678、03-1234-5678"}
                                size="small"
                                InputProps={{
                                    startAdornment: <LocalPhoneIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        minHeight: { xs: 56, sm: 48 }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="メールアドレス"
                                fullWidth
                                placeholder="例：example@company.com"
                                value={clientData.email}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                                error={!!errorMessages.email}
                                helperText={errorMessages.email || "例：example@company.com"}
                                type="email"
                                size="small"
                                InputProps={{
                                    startAdornment: <EmailIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        minHeight: { xs: 56, sm: 48 }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* 入力ヒントメッセージ */}
                    <Alert severity="info" sx={{ my: 3 }}>
                        <Typography variant="body2">
                            <strong>便利な機能：</strong><br />
                            • 郵便番号を入力すると自動で住所を検索します<br />
                            • 「（株）」「（有）」などは全角で入力してください<br />
                            • 連絡先情報は任意です（後から追加可能）
                        </Typography>
                    </Alert>

                    <Box sx={{ textAlign: 'right' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled()}
                            size="large"
                            sx={{ 
                                minWidth: 120,
                                minHeight: { xs: 48, sm: 40 }
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "登録"}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </>
    );
};

export default ClientRegistrationStep1;